import { MessageSquareText, UserRound } from 'lucide-react';
import { DetailHero } from '@/components/common/DetailHero';
import { DetailInfoCard } from '@/components/common/DetailInfoCard';
import { DetailValue } from '@/components/common/DetailValue';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { PersonDetailLines } from '@/components/common/PersonDetailLines';
import { Skeleton } from '@/components/ui/skeleton';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { ExpenseApprovalDetail } from '../types/billing.types';
import { FiscalYearBadge } from './FiscalYearBadge';
import { VerifyStatusBadge } from './VerifyStatusBadge';

interface ExpenseSummaryProps {
  expense: ExpenseApprovalDetail | undefined;
}

function renderFiscalYear(expense: ExpenseApprovalDetail | undefined) {
  if (expense === undefined) return <Skeleton className="h-5 w-24" />;
  if (!expense.fiscalYear) return null;
  return <FiscalYearBadge fiscalYear={expense.fiscalYear} />;
}

export function ExpenseSummary({ expense }: ExpenseSummaryProps) {
  const isLoading = expense === undefined;

  return (
    <div className="flex flex-col gap-4">
      <DetailHero
        heading={
          expense ? (
            <div className="flex items-center gap-2.5">
              <span className="text-lg font-semibold tracking-tight">
                {formatDisplayDate(expense.expenseDate)}
              </span>
              <VerifyStatusBadge status={expense.approveStatus} decisionKind="approve" />
            </div>
          ) : (
            <Skeleton className="h-7 w-52" />
          )
        }
        subline={renderFiscalYear(expense)}
        amountLabel="Claimed"
        amount={expense?.claimedTotal}
        amountNote={
          <DetailValue isLoading={isLoading}>
            <span className="text-xs text-muted-foreground tabular-nums">
              Approved so far {formatNumber(expense?.approvedTotal ?? 0)} {TAKA_SYMBOL}
            </span>
          </DetailValue>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <DetailInfoCard label="ZM" icon={UserRound}>
          <LabelWithValue label="ZM">
            <DetailValue isLoading={isLoading}>
              {expense ? (
                <PersonDetailLines
                  name={expense.name}
                  designationName={expense.designationName}
                  mobile={expense.userMobile}
                />
              ) : null}
            </DetailValue>
          </LabelWithValue>
        </DetailInfoCard>

        <DetailInfoCard label="Remarks" icon={MessageSquareText}>
          <LabelWithValue label="Remarks">
            <DetailValue isLoading={isLoading}>{expense?.remarks}</DetailValue>
          </LabelWithValue>
        </DetailInfoCard>
      </div>
    </div>
  );
}
